import { Byte, Charset, Encoder } from '@nuintun/qrcode';
import { isNoQrBank } from '@p2p/entities/qrInvoice/noQrBanks';
import { getProviderQrImageUrl } from '@p2p/entities/qrInvoice/providerQrImage';
import { generateQrInvoiceForSber, isSberUzbekistan } from '@p2p/entities/qrInvoice/sber';
import { TGetOrderResponse } from '@p2p/services';

import {
  buildQrData,
  encodeReadyQrPayload,
  generateQRCodeValue,
  isSberQrInvoice,
} from './QRCodeInvoice.utils';

/**
 * Что показать в QR на экране «Оплата по QR-коду»: готовую картинку (`image`) или строку,
 * которую кодируем сами (`value`). `null` - QR у заявки нет (банк без QR).
 *
 * P2P-3116: вынесено из QrCodeInvoice, чтобы страница alfa_page рисовала QR по тем же правилам.
 */
export type QrInvoiceSource = { kind: 'image'; src: string } | { kind: 'value'; value: string };

// P2P-2580: приложение Сбера читает содержимое QR как UTF-8, но ТОЛЬКО если в QR есть маркер
// ECI-26 (UTF-8). Библиотека react-qr-code кодирует в режиме Byte без ECI, который по стандарту
// трактуется как Latin-1 → кириллица в приложении Сбера превращается в мусор («?»/«°¶»).
// Поэтому для Сбера рендерим QR через @nuintun/qrcode с Charset.UTF_8 (ставит ECI-26).
// Остальные банки/EMVCo — как прежде, через react-qr-code (поведение не меняется).
const buildSberQrDataUrl = (order: TGetOrderResponse, randomId: string): string => {
  const qrData = buildQrData(order, randomId);
  const qrcode = new Encoder({ level: 'L' }).encode(new Byte(qrData, Charset.UTF_8));

  return qrcode.toDataURL(8, { margin: 4 });
};

export const getQrInvoiceSource = (
  order: TGetOrderResponse,
  randomId: string,
): QrInvoiceSource | null => {
  // P2P-2739: если провайдер прислал готовую ссылку на оплату (Phoneix
  // invoice_url) — кодируем в QR её, локальные шаблоны не применяем.
  const hasInvoiceUrl = Boolean(order.invoice_url);
  // P2P-3043: готовая QR-картинка провайдера (Viatrum PAYMENT_LINK) — показываем её как
  // есть, ничего не собираем и не перекодируем.
  const providerQrImageUrl = getProviderQrImageUrl(order);

  // Babilon-M (Таджикистан): провайдер не поддерживает QR — на странице
  // остаются только реквизиты (AccountNumberInfoItems) и инструкция.
  // P2P-2739: для банков без собственного QR (Babilon-M) исключение — если
  // провайдер прислал ссылку на оплату, QR по ней показываем.
  if (isNoQrBank(order) && !hasInvoiceUrl && !providerQrImageUrl) {
    return null;
  }

  if (providerQrImageUrl) {
    return { kind: 'image', src: providerQrImageUrl };
  }

  // P2P-2846: реквизит YouTrade Альфа QR (и аналогов) технический -- у него
  // requisite.qr_invoice=true (нужно для мерчантского QR, см. get_qr_code на
  // бэке), но никаких банковских полей на самом деле нет, только ссылка
  // (order.ios_link/android_link). Без этого исключения такая заявка могла
  // случайно попасть в isSber (если ПЛАТЕЛЬЩИК выбрал client_bank_code=sber
  // для быстрой оплаты -- это его личный выбор, не признак провайдера) и
  // получить QR, собранный из тех же полей-заглушек, что и в общем случае ниже.
  const isPayViaLink = Boolean(order.payment_via_link && (order.ios_link || order.android_link));

  if (order.invoice_url) {
    // P2P-2739: ссылка на оплату от провайдера (Phoneix) — в QR попадает URL.
    return { kind: 'value', value: order.invoice_url };
  }

  if (isPayViaLink) {
    // P2P-2846: провайдер (YouTrade Альфа QR и аналоги -- см.
    // order.payment_via_link, api/v1/serializers/order.py::get_payment_via_link)
    // не отдаёт ни карту, ни телефон -- только ссылку на QR. Кодируем ссылку
    // как есть; банковский ST0001x-шаблон ниже (generateQRCodeValue) сюда не
    // подходит -- полям PersonalAcc/Name/BIC и т.д. взяться неоткуда, реквизит
    // технический (жалоба интеграционного партнёра 17.09.2026: страница
    // генерировала свой QR из этих полей-заглушек вместо ссылки провайдера).
    return { kind: 'value', value: order.ios_link || order.android_link || '' };
  }

  if (order.emvco) {
    // Готовая строка платёжного QR от провайдера: EMVCo (SberPay QR от
    // SimplePay, P2P-2499/2500) или ГОСТ (YouTrade Альфа QR, P2P-2846, в
    // магазине выбран формат выдачи «сырые данные»). Ничего не собираем сами
    // — только приводим байты к кодировке, объявленной в служебном теге
    // ГОСТ-строки (EMVCo это не касается, он ASCII).
    return { kind: 'value', value: encodeReadyQrPayload(order.emvco) };
  }

  if (isSberQrInvoice(order)) {
    return { kind: 'image', src: buildSberQrDataUrl(order, randomId) };
  }

  // P2P-2453: сберовские заявки на банки Узбекистана (SBP) используют собственный
  // deeplink-формат (transfers/abroad?...), а не GOST/ECI-26 шаблон P2P-2580 — рендерим
  // как обычный react-qr-code, отдельной веткой от isSber (qr_invoice).
  if (isSberUzbekistan(order)) {
    return { kind: 'value', value: generateQrInvoiceForSber(order) };
  }

  return { kind: 'value', value: generateQRCodeValue(order, randomId) };
};
